import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type Dispatch,
  type SetStateAction,
} from 'react'

import {
  addNodeAvailableModel,
  getModelArtifactByRelease,
  removeNodeAvailableModel,
} from '@/api/models'
import { listNodes } from '@/api/nodes'
import type { ReleaseManifest } from '@/api/guardianforge/types'
import { useAuth } from '@/auth/useAuth'
import { DeploymentSpinner } from '@/components/deployment/DeploymentUi'
import type {
  ModelArtifactByReleaseResponse,
  NodeSummary,
} from '@/types'
import {
  runAssignmentBatch,
  type AssignmentBatchResult,
} from './modelAssignmentBatch'

const NODE_PICKER_LIMIT = 1000
const UNASSIGNED_COMPANY = 'Unassigned'
const NO_SITE = 'No site/area'

type AssignmentOperation = 'add' | 'remove'

interface BatchFailure {
  nodeId: string
  reason: string
}

interface BatchSummary {
  operation: AssignmentOperation
  succeeded: number
  failures: BatchFailure[]
}

interface ModelAssignmentPanelProps {
  release: ReleaseManifest
  onMutationBusyChange?: (busy: boolean) => void
}

function nodeCompany(node: NodeSummary): string {
  return node.company?.trim() || UNASSIGNED_COMPANY
}

function nodeSite(node: NodeSummary): string {
  return node.site?.trim() || NO_SITE
}

function nodeDisplayName(node: NodeSummary): string {
  const name = node.name?.trim()
  return name ? `${name} (${node.node_key})` : node.node_key
}

function errorStatus(error: unknown): number | null {
  if (typeof error === 'object' && error !== null && 'status' in error) {
    const status = Number((error as { status?: unknown }).status)
    return Number.isFinite(status) ? status : null
  }
  return null
}

function errorMessage(error: unknown, fallback: string): string {
  if (error instanceof Error && error.message) return error.message
  if (typeof error === 'object' && error !== null && 'message' in error) {
    const message = String((error as { message?: unknown }).message ?? '').trim()
    if (message) return message
  }
  return fallback
}

function assignmentFailureReason(
  operation: AssignmentOperation,
  reason: unknown,
): string {
  if (operation === 'remove' && errorStatus(reason) === 409) {
    return '이 모델을 쓰는 카메라 mapping이 남아 있습니다'
  }
  return errorMessage(reason, '노드 배정 요청에 실패했습니다')
}

export default function ModelAssignmentPanel({
  release,
  onMutationBusyChange,
}: ModelAssignmentPanelProps) {
  const { hasPermission } = useAuth()
  const canManageAssignments = hasPermission('model-deployment:execute')
  const releaseKey = `${release.model_key}\u0000${release.version}`
  const activeReleaseKey = useRef(releaseKey)
  activeReleaseKey.current = releaseKey

  const [nodes, setNodes] = useState<NodeSummary[]>([])
  const [nodesTotal, setNodesTotal] = useState(0)
  const [nodesLoading, setNodesLoading] = useState(true)
  const [nodesError, setNodesError] = useState<unknown>(null)
  const nodesRequestSequence = useRef(0)

  const [assignment, setAssignment] = useState<ModelArtifactByReleaseResponse | null>(null)
  const [assignedNodeIds, setAssignedNodeIds] = useState<Set<string>>(() => new Set())
  const [assignmentLoading, setAssignmentLoading] = useState(true)
  const [assignmentError, setAssignmentError] = useState<unknown>(null)
  const [catalogMissing, setCatalogMissing] = useState(false)
  const assignmentRequestSequence = useRef(0)

  const [companyFilter, setCompanyFilter] = useState('')
  const [siteFilter, setSiteFilter] = useState('')
  const [addSelection, setAddSelection] = useState<Set<string>>(() => new Set())
  const [removeSelection, setRemoveSelection] = useState<Set<string>>(() => new Set())
  const [batchOperation, setBatchOperation] = useState<AssignmentOperation | null>(null)
  const [batchProgress, setBatchProgress] = useState({ completed: 0, total: 0 })
  const [batchSummary, setBatchSummary] = useState<BatchSummary | null>(null)

  const loadNodes = useCallback(async () => {
    const sequence = ++nodesRequestSequence.current
    setNodesLoading(true)
    setNodesError(null)
    try {
      const response = await listNodes({ limit: NODE_PICKER_LIMIT })
      if (sequence !== nodesRequestSequence.current) return
      setNodes(response.items)
      setNodesTotal(response.total)
    } catch (error: unknown) {
      if (sequence !== nodesRequestSequence.current) return
      setNodesError(error)
    } finally {
      if (sequence === nodesRequestSequence.current) {
        setNodesLoading(false)
      }
    }
  }, [])

  const applyAssignmentResponse = useCallback((response: ModelArtifactByReleaseResponse) => {
    const nextAssigned = new Set(response.assigned_node_ids)
    setAssignment(response)
    setAssignedNodeIds(nextAssigned)
    setAddSelection((current) => new Set([...current].filter((nodeId) => !nextAssigned.has(nodeId))))
    setRemoveSelection((current) => new Set([...current].filter((nodeId) => nextAssigned.has(nodeId))))
  }, [])

  const loadAssignment = useCallback(async (preserveCurrent = false) => {
    const requestedReleaseKey = releaseKey
    const sequence = ++assignmentRequestSequence.current
    setAssignmentLoading(true)
    setAssignmentError(null)
    setCatalogMissing(false)
    try {
      const response = await getModelArtifactByRelease(release.model_key, release.version)
      if (
        sequence !== assignmentRequestSequence.current
        || requestedReleaseKey !== activeReleaseKey.current
      ) return
      applyAssignmentResponse(response)
    } catch (error: unknown) {
      if (
        sequence !== assignmentRequestSequence.current
        || requestedReleaseKey !== activeReleaseKey.current
      ) return
      const missing = errorStatus(error) === 404
      setCatalogMissing(missing)
      setAssignmentError(missing ? { message: 'NM 카탈로그 미등록' } : error)
      if (!preserveCurrent) {
        setAssignment(null)
        setAssignedNodeIds(new Set())
      }
    } finally {
      if (
        sequence === assignmentRequestSequence.current
        && requestedReleaseKey === activeReleaseKey.current
      ) {
        setAssignmentLoading(false)
      }
    }
  }, [applyAssignmentResponse, release.model_key, release.version, releaseKey])

  useEffect(() => {
    loadNodes()
  }, [loadNodes])

  useEffect(() => {
    assignmentRequestSequence.current += 1
    setAssignment(null)
    setAssignedNodeIds(new Set())
    setAssignmentError(null)
    setCatalogMissing(false)
    setAddSelection(new Set())
    setRemoveSelection(new Set())
    setBatchSummary(null)
    setCompanyFilter('')
    setSiteFilter('')
    loadAssignment(false)
  }, [loadAssignment, releaseKey])

  useEffect(() => () => {
    onMutationBusyChange?.(false)
  }, [onMutationBusyChange])

  const nodesById = useMemo(
    () => new Map(nodes.map((node) => [node.id, node])),
    [nodes],
  )
  const companies = useMemo(
    () => [...new Set(nodes.map(nodeCompany))].sort((a, b) => a.localeCompare(b)),
    [nodes],
  )
  const sites = useMemo(
    () => [...new Set(
      nodes
        .filter((node) => !companyFilter || nodeCompany(node) === companyFilter)
        .map(nodeSite),
    )].sort((a, b) => a.localeCompare(b)),
    [companyFilter, nodes],
  )
  const filteredNodes = useMemo(
    () => nodes.filter((node) => {
      if (companyFilter && nodeCompany(node) !== companyFilter) return false
      if (siteFilter && nodeSite(node) !== siteFilter) return false
      return true
    }),
    [companyFilter, nodes, siteFilter],
  )
  const addCandidates = useMemo(
    () => filteredNodes.filter((node) => !assignedNodeIds.has(node.id)),
    [assignedNodeIds, filteredNodes],
  )
  const visibleAddCandidateIds = useMemo(
    () => new Set(addCandidates.map((node) => node.id)),
    [addCandidates],
  )
  const hiddenAddSelectionCount = useMemo(
    () => [...addSelection].filter((nodeId) => !visibleAddCandidateIds.has(nodeId)).length,
    [addSelection, visibleAddCandidateIds],
  )
  const assignedEntries = useMemo(
    () => [...assignedNodeIds]
      .map((nodeId) => ({ nodeId, node: nodesById.get(nodeId) ?? null }))
      .sort((left, right) => {
        const leftLabel = left.node ? nodeDisplayName(left.node) : left.nodeId
        const rightLabel = right.node ? nodeDisplayName(right.node) : right.nodeId
        return leftLabel.localeCompare(rightLabel)
      }),
    [assignedNodeIds, nodesById],
  )

  function toggleSelection(
    setter: Dispatch<SetStateAction<Set<string>>>,
    nodeId: string,
    checked: boolean,
  ) {
    setter((current) => {
      const next = new Set(current)
      if (checked) next.add(nodeId)
      else next.delete(nodeId)
      return next
    })
  }

  async function runBatch(operation: AssignmentOperation) {
    if (!assignment || batchOperation) return
    const targetIds = [
      ...(operation === 'add' ? addSelection : removeSelection),
    ]
    if (targetIds.length === 0) return

    const requestedReleaseKey = releaseKey
    const artifactId = assignment.artifact.id
    setBatchOperation(operation)
    setBatchProgress({ completed: 0, total: targetIds.length })
    setBatchSummary(null)
    onMutationBusyChange?.(true)
    try {
      const results = await runAssignmentBatch(targetIds, async (nodeId) => {
        try {
          if (operation === 'add') {
            await addNodeAvailableModel(nodeId, artifactId)
          } else {
            await removeNodeAvailableModel(nodeId, artifactId)
          }
        } finally {
          setBatchProgress((current) => ({
            ...current,
            completed: current.completed + 1,
          }))
        }
      })
      if (requestedReleaseKey !== activeReleaseKey.current) return

      const succeededNodeIds = results
        .filter((result) => result.status === 'fulfilled')
        .map((result) => result.nodeId)
      const failures = results
        .filter((result): result is AssignmentBatchResult & { status: 'rejected' } => (
          result.status === 'rejected'
        ))
        .map((result) => ({
          nodeId: result.nodeId,
          reason: assignmentFailureReason(operation, result.reason),
        }))

      setAssignedNodeIds((current) => {
        const next = new Set(current)
        for (const nodeId of succeededNodeIds) {
          if (operation === 'add') next.add(nodeId)
          else next.delete(nodeId)
        }
        return next
      })
      const failedNodeIds = new Set(failures.map((failure) => failure.nodeId))
      if (operation === 'add') {
        setAddSelection(failedNodeIds)
      } else {
        setRemoveSelection(failedNodeIds)
      }
      setBatchSummary({
        operation,
        succeeded: succeededNodeIds.length,
        failures,
      })
      await loadAssignment(true)
    } finally {
      if (requestedReleaseKey === activeReleaseKey.current) {
        setBatchOperation(null)
        onMutationBusyChange?.(false)
      }
    }
  }

  const mutationUnavailable = !canManageAssignments
    || !assignment
    || assignmentLoading
    || batchOperation !== null
  const addDisabled = mutationUnavailable
    || !assignment?.artifact.deployable
    || nodesLoading
    || Boolean(nodesError)
    || addSelection.size === 0
  const removeDisabled = mutationUnavailable || removeSelection.size === 0

  return (
    <section className="deployment-assignment-section" aria-label="노드 배정">
      <div className="deployment-flex deployment-between">
        <div>
          <h3 className="deployment-assignment-title">노드 배정</h3>
          <div className="deployment-muted deployment-assignment-hint">
            노드의 모델 선택 목록에 추가합니다. 카메라 mapping이나 배포 명령은 생성하지 않습니다.
          </div>
        </div>
        {(assignmentLoading || batchOperation) && <DeploymentSpinner />}
      </div>

      {assignmentError != null && (
        <div className="deployment-error-box with-actions">
          <span>{errorMessage(assignmentError, '배정 정보를 불러오지 못했습니다')}</span>
          <button
            type="button"
            disabled={assignmentLoading || batchOperation !== null}
            onClick={() => loadAssignment(Boolean(assignment))}
          >
            다시 시도
          </button>
        </div>
      )}
      {catalogMissing && (
        <div className="deployment-muted deployment-assignment-hint">
          이 릴리스가 NM 카탈로그에 등록된 뒤 배정할 수 있습니다.
        </div>
      )}
      {assignment && !assignment.artifact.deployable && (
        <div className="deployment-error-box">
          이 릴리스는 배포 가능한 metadata가 없어 새 노드에 배정할 수 없습니다.
          기존 배정 해제는 가능합니다.
        </div>
      )}
      {!canManageAssignments && assignment && (
        <div className="deployment-muted deployment-assignment-hint">
          배정 변경에는 model-deployment:execute 권한이 필요합니다.
        </div>
      )}

      <div className="deployment-assignment-filters">
        <label>
          <span>회사</span>
          <select
            aria-label="배정 노드 회사 필터"
            value={companyFilter}
            disabled={nodesLoading || batchOperation !== null}
            onChange={(event) => {
              setCompanyFilter(event.target.value)
              setSiteFilter('')
            }}
          >
            <option value="">전체 회사</option>
            {companies.map((company) => (
              <option key={company} value={company}>{company}</option>
            ))}
          </select>
        </label>
        <label>
          <span>사이트/구역</span>
          <select
            aria-label="배정 노드 사이트 필터"
            value={siteFilter}
            disabled={nodesLoading || batchOperation !== null}
            onChange={(event) => setSiteFilter(event.target.value)}
          >
            <option value="">전체 사이트/구역</option>
            {sites.map((site) => (
              <option key={site} value={site}>{site}</option>
            ))}
          </select>
        </label>
      </div>

      {nodesLoading && (
        <div className="deployment-muted deployment-assignment-state">노드 목록을 불러오는 중...</div>
      )}
      {nodesError != null && (
        <div className="deployment-error-box with-actions">
          <span>{errorMessage(nodesError, '노드 목록을 불러오지 못했습니다')}</span>
          <button
            type="button"
            disabled={nodesLoading || batchOperation !== null}
            onClick={loadNodes}
          >
            노드 목록 다시 시도
          </button>
        </div>
      )}
      {nodesTotal > nodes.length && (
        <div className="deployment-error-box">
          전체 {nodesTotal}개 중 {nodes.length}개 노드만 표시됩니다.
        </div>
      )}

      <div className="deployment-assignment-columns">
        <div>
          <div className="deployment-assignment-column-title">
            배정 가능한 노드 ({addCandidates.length})
          </div>
          <div className="deployment-assignment-node-list">
            {addCandidates.map((node) => (
              <label key={node.id} className="deployment-assignment-node">
                <input
                  type="checkbox"
                  aria-label={`배정 대상 ${nodeDisplayName(node)}`}
                  checked={addSelection.has(node.id)}
                  disabled={
                    !canManageAssignments
                    || !assignment
                    || !assignment.artifact.deployable
                    || batchOperation !== null
                  }
                  onChange={(event) => {
                    toggleSelection(setAddSelection, node.id, event.target.checked)
                  }}
                />
                <span>
                  <span className="deployment-assignment-node-name">{nodeDisplayName(node)}</span>
                  <span className="deployment-muted">
                    {nodeCompany(node)} / {nodeSite(node)} · {node.status}
                  </span>
                </span>
              </label>
            ))}
            {!nodesLoading && !nodesError && addCandidates.length === 0 && (
              <div className="deployment-muted deployment-assignment-empty">
                현재 필터에서 배정 가능한 노드가 없습니다.
              </div>
            )}
          </div>
          {hiddenAddSelectionCount > 0 && (
            <div className="deployment-muted deployment-assignment-selection-note">
              현재 필터 밖에서 선택한 {hiddenAddSelectionCount}개 노드도 함께 배정됩니다.
            </div>
          )}
          <button
            type="button"
            disabled={addDisabled}
            onClick={() => runBatch('add')}
          >
            선택 노드에 배정 ({addSelection.size})
          </button>
        </div>

        <div>
          <div className="deployment-assignment-column-title">
            이미 배정된 노드 ({assignedEntries.length})
          </div>
          <div className="deployment-assignment-node-list">
            {assignedEntries.map(({ nodeId, node }) => (
              <label key={nodeId} className="deployment-assignment-node">
                <input
                  type="checkbox"
                  aria-label={`배정 해제 대상 ${node ? nodeDisplayName(node) : nodeId}`}
                  checked={removeSelection.has(nodeId)}
                  disabled={!canManageAssignments || !assignment || batchOperation !== null}
                  onChange={(event) => {
                    toggleSelection(setRemoveSelection, nodeId, event.target.checked)
                  }}
                />
                <span>
                  <span className="deployment-assignment-node-name">
                    {node ? nodeDisplayName(node) : nodeId}
                  </span>
                  <span className="deployment-muted">
                    {node
                      ? `${nodeCompany(node)} / ${nodeSite(node)} · ${node.status}`
                      : '노드 목록 범위 밖'}
                  </span>
                </span>
              </label>
            ))}
            {!assignmentLoading && assignedEntries.length === 0 && (
              <div className="deployment-muted deployment-assignment-empty">
                이 릴리스가 배정된 노드가 없습니다.
              </div>
            )}
          </div>
          <button
            type="button"
            className="danger"
            disabled={removeDisabled}
            onClick={() => runBatch('remove')}
          >
            선택 배정 해제 ({removeSelection.size})
          </button>
        </div>
      </div>

      {batchOperation && (
        <div className="deployment-assignment-progress" role="status">
          {batchOperation === 'add' ? '배정' : '배정 해제'} 진행 중: {batchProgress.completed}/{batchProgress.total}
        </div>
      )}
      {batchSummary && (
        <div className="deployment-assignment-summary" role="status">
          <div>
            {batchSummary.operation === 'add' ? '배정' : '배정 해제'} 완료:
            {' '}{batchSummary.succeeded}개 성공, {batchSummary.failures.length}개 실패
          </div>
          {batchSummary.failures.length > 0 && (
            <ul>
              {batchSummary.failures.map((failure) => {
                const node = nodesById.get(failure.nodeId)
                return (
                  <li key={failure.nodeId}>
                    <strong>{node ? nodeDisplayName(node) : failure.nodeId}</strong>: {failure.reason}
                  </li>
                )
              })}
            </ul>
          )}
        </div>
      )}
    </section>
  )
}
